/** SUNTRONIX ONE — ONE GROUP: consolidated Group Dashboard (management).
 *  Compares revenue, orders, stock and receivables across every establishment.
 */
import { useEffect, useState } from "react";
import { Building2, Loader2, TrendingUp, ClipboardList, Boxes, Receipt, FileText } from "lucide-react";
import api from "@/lib/api";

const fmtINR = (n) => n == null ? "—" :
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);
const fmtNum = (n) => new Intl.NumberFormat("en-IN").format(n || 0);

const METRICS = [
  { key: "orders", label: "Order revenue", icon: TrendingUp, tint: "text-emerald-600" },
  { key: "customer_pos", label: "Customer POs", icon: ClipboardList, tint: "text-sky-600" },
  { key: "quotes", label: "Quotations", icon: FileText, tint: "text-violet-600" },
  { key: "receivables", label: "Receivables", icon: Receipt, tint: "text-amber-600" },
  { key: "stock", label: "Stock (units)", icon: Boxes, tint: "text-cyan-600" },
];

export default function GroupDashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/group/dashboard").then(({ data }) => setData(data))
      .catch((e) => setErr(e.response?.data?.detail || "Failed to load Group Dashboard"));
  }, []);

  if (err) return <div className="p-6 text-[13px] text-rose-600" data-testid="group-dashboard-error">{err}</div>;
  if (!data) return <div className="p-6 text-slate-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  const { rows, totals } = data;

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="group-dashboard-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-slate-900 grid place-items-center text-white"><Building2 className="h-5 w-5" /></div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Group Dashboard</h1>
          <p className="text-[13px] text-slate-500">ONE GROUP — revenue, orders, stock and receivables compared across every establishment.</p>
        </div>
      </div>

      {/* Group totals */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="group-totals">
        {METRICS.map((m) => {
          const t = totals[m.key] || { count: 0, value: 0 };
          const Icon = m.icon;
          return (
            <div key={m.key} className="rounded-xl border border-slate-200 bg-white p-3.5" data-testid={`group-total-${m.key}`}>
              <div className="flex items-center gap-1.5 text-[11px] text-slate-500 font-medium"><Icon className={"h-3.5 w-3.5 " + m.tint} />{m.label}</div>
              <div className="text-[18px] font-bold text-slate-900 mt-1 tabular-nums">
                {m.key === "stock" ? fmtNum(t.count) : fmtINR(t.value)}
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5">
                {m.key === "stock" ? "units in stock" : fmtNum(t.count) + " record(s)"}
              </div>
            </div>
          );
        })}
      </div>

      {/* Per-establishment comparison */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]" data-testid="group-comparison-table">
            <thead>
              <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2.5 font-semibold">Establishment</th>
                <th className="px-4 py-2.5 font-semibold text-right">Order revenue</th>
                <th className="px-4 py-2.5 font-semibold text-right">Customer POs</th>
                <th className="px-4 py-2.5 font-semibold text-right">Quotations</th>
                <th className="px-4 py-2.5 font-semibold text-right">Receivables</th>
                <th className="px-4 py-2.5 font-semibold text-right">Stock units</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.establishment_id} className="hover:bg-slate-50" data-testid={`group-row-${r.establishment_id}`}>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: r.primary_color }} />
                      <span className="font-semibold text-slate-800">{r.display_name}</span>
                      <span className="text-[10px] text-slate-400 font-mono">{r.short_name}</span>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium text-slate-900">{fmtINR(r.orders.value)}<span className="block text-[10px] text-slate-400">{fmtNum(r.orders.count)}</span></td>
                  <td className="px-4 py-3 text-right tabular-nums">{fmtINR(r.customer_pos.value)}<span className="block text-[10px] text-slate-400">{fmtNum(r.customer_pos.count)}</span></td>
                  <td className="px-4 py-3 text-right tabular-nums">{fmtINR(r.quotes.value)}<span className="block text-[10px] text-slate-400">{fmtNum(r.quotes.count)}</span></td>
                  <td className="px-4 py-3 text-right tabular-nums text-amber-700">{fmtINR(r.receivables.value)}<span className="block text-[10px] text-slate-400">{fmtNum(r.receivables.count)}</span></td>
                  <td className="px-4 py-3 text-right tabular-nums">{fmtNum(r.stock.count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-[11px] text-slate-400">Historical records created before ONE GROUP are attributed to SUNTRONIX.</p>
    </div>
  );
}
